import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, options: { sitekey: string; callback: (token: string) => void; 'error-callback'?: () => void; theme?: 'auto' | 'light' | 'dark' }) => string;
      reset: (id?: string) => void;
    };
  }
}

interface TurnstileBoxProps {
  siteKey?: string;
  devBypass?: boolean;
  onToken: (token: string) => void;
}

export function TurnstileBox({ siteKey, devBypass, onToken }: TurnstileBoxProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | undefined>(undefined);
  const [status, setStatus] = useState<'waiting' | 'ready' | 'verified' | 'dev' | 'error'>('waiting');

  useEffect(() => {
    if (!siteKey) {
      if (devBypass) {
        setStatus('dev');
        onToken('dev-bypass');
      } else {
        setStatus('error');
      }
      return;
    }
    let mounted = true;
    const render = () => {
      if (!mounted || !ref.current || !window.turnstile || widgetId.current) return;
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        theme: 'dark',
        callback: (token) => {
          setStatus('verified');
          onToken(token);
        },
        'error-callback': () => setStatus('error')
      });
      setStatus('ready');
    };
    if (!document.querySelector('script[data-turnstile]')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.turnstile = 'true';
      script.onload = render;
      document.head.appendChild(script);
    } else {
      const wait = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(wait);
          render();
        }
      }, 100);
      return () => window.clearInterval(wait);
    }
    return () => {
      mounted = false;
    };
  }, [siteKey, devBypass, onToken]);

  if (status === 'dev') return <div className="turnstile dev">Local dev human check bypassed.</div>;
  if (status === 'error') return <div className="turnstile error">Turnstile is not configured. Add site and secret keys before production submit.</div>;
  return <div className="turnstile"><div ref={ref} /><small>{status === 'verified' ? 'Human signal verified.' : 'Human check loads here before commons submit.'}</small></div>;
}
